import React, { useState, useEffect } from 'react';
import { TextField, Button, IconButton, Tooltip, Dialog, DialogTitle, DialogContent, DialogActions, CircularProgress } from '@mui/material';
import { toast } from 'react-toastify';
import VideocamIcon from '@mui/icons-material/Videocam';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import { DateTimePicker } from '@mui/x-date-pickers/DateTimePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { ZoomMeeting } from '../index';
import axios from 'axios';
import { SECRETS_ENV } from '../../../Config/firebase';
import { collection, onSnapshot } from 'firebase/firestore';
import { db } from '../../../Config/firebase';
import { useAppDispatch, useAppSelector } from '../../../state/state';
import { revokeZoomAccess, checkZoomAuth } from '../../../state/ZoomSlice/zoomSlice';
import { RootState } from '../../../state/state';

const API_ENDPOINT = SECRETS_ENV.API_END_POINT;

interface ZoomMeetingSectionProps {
    serviceProviderId: string;
    subscriptionId: string;
    subscriptionName: string;
    userRole?: string;
}

const ZoomMeetingSection: React.FC<ZoomMeetingSectionProps> = ({
    serviceProviderId,
    subscriptionId,
    subscriptionName,
    userRole
}) => {
    const [showCreateModal, setShowCreateModal] = useState(false);
    const [showDisconnectModal, setShowDisconnectModal] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const [meetings, setMeetings] = useState<ZoomMeeting[]>([]);
    const [isLoadingMeetings, setIsLoadingMeetings] = useState(false);
    const [formData, setFormData] = useState({
        topic: '',
        startTime: new Date(),
        duration: 60,
        type: 'one-time' as 'one-time' | 'recurring',
        weekDays: [] as number[],
        description: '',
        recurring: false,
        recurringDays: [] as number[]
    });
    const [editingMeetingId, setEditingMeetingId] = useState<string | null>(null);

    // Add dispatch and zoom state
    const dispatch = useAppDispatch();
    const zoomState = useAppSelector((state: RootState) => state.zoomSlice.data) || {
        isAuthenticated: false,
        userEmail: null
    };
    const [isCheckingZoom, setIsCheckingZoom] = useState(false);

    // Check Zoom authentication status when component mounts and periodically
    useEffect(() => {
        const checkZoomStatus = async () => {
            if (!serviceProviderId) return;
            
            try {
                await dispatch(checkZoomAuth(serviceProviderId));
            } catch (error) {
                console.error('Error checking Zoom auth status:', error);
            }
        };

        // Check immediately when component mounts
        checkZoomStatus();

        // Set up interval to check every 30 seconds for the first 2 minutes after mount
        // This helps catch newly connected accounts
        const intervals: NodeJS.Timeout[] = [];
        
        // Check every 10 seconds for the first 2 minutes
        for (let i = 1; i <= 12; i++) {
            const interval = setTimeout(() => {
                checkZoomStatus();
            }, i * 10000); // 10, 20, 30... up to 120 seconds
            intervals.push(interval);
        }

        // Then check every 5 minutes after that
        const longInterval = setInterval(checkZoomStatus, 5 * 60 * 1000);

        return () => {
            intervals.forEach(interval => clearTimeout(interval));
            clearInterval(longInterval);
        };
    }, [serviceProviderId, dispatch]);

    // Fetch meetings from Firestore
    useEffect(() => {
        if (!serviceProviderId || !subscriptionId) return;
        
        setIsLoadingMeetings(true);
        
        const meetingsRef = collection(
            db, 
            'ServiceProviders', serviceProviderId, 
            'Subscriptions', subscriptionId, 
            'Meetings'
        );
        
        // Use onSnapshot to get real-time updates
        const unsubscribe = onSnapshot(meetingsRef, (snapshot) => {
            const meetingsData: ZoomMeeting[] = [];
            
            snapshot.forEach((doc) => {
                const data = doc.data();
                
                // Only include meetings that are Zoom type
                if (data.type !== 'ZOOM') {
                    return; // Skip this meeting if it's not a Zoom meeting
                }
                
                // Convert Firestore timestamps to strings
                const startTime = data.startTime?.toDate().toISOString() || new Date().toISOString();
                
                // Map the Firestore data to the ZoomMeeting type
                meetingsData.push({
                    id: doc.id,
                    meetingId: data.eventId || '',
                    password: data.password || '',
                    topic: data.title || '',
                    startTime: startTime,
                    duration: data.duration || 60,
                    joinUrl: data.meetLink || '',
                    createdAt: data.createdAt?.toDate().getTime() || Date.now(),
                    type: data.recurring ? 'recurring' : 'one-time',
                    weekDays: data.recurringDays || [],
                    description: data.description || ''
                });
            });
            
            // Sort meetings by start time (newest first)
            meetingsData.sort((a, b) => new Date(b.startTime).getTime() - new Date(a.startTime).getTime());
            
            setMeetings(meetingsData);
            setIsLoadingMeetings(false);
        }, (error) => {
            console.error('Error fetching meetings:', error);
            toast.error('Failed to load meetings');
            setIsLoadingMeetings(false);
        });
        
        return () => unsubscribe();
    }, [serviceProviderId, subscriptionId]);

    const handleCreateMeeting = async () => {
        try {
            setIsLoading(true);
            
            // Create meeting using our backend API
            const response = await axios.post(`${API_ENDPOINT}/api/zoom/create-meeting`, {
                userId: serviceProviderId,
                subId: subscriptionId,
                topic: formData.topic || `${subscriptionName} Meeting`,
                startTime: formData.startTime.toISOString(),
                duration: formData.duration,
                type: formData.type,
                weekDays: formData.weekDays,
                description: formData.description,
                recurring: formData.type === 'recurring',
                recurringDays: formData.weekDays
            });

            // Check if response has the expected data
            if (response.data && response.data.meetLink) {
                setShowCreateModal(false);
                setFormData({
                    topic: '',
                    startTime: new Date(),
                    duration: 60,
                    type: 'one-time',
                    weekDays: [],
                    description: '',
                    recurring: false,
                    recurringDays: []
                });
                toast.success('Zoom meeting created successfully');
            } else {
                console.error('Unexpected API response:', response.data);
                throw new Error('Invalid response from server');
            }
        } catch (error: any) {
            console.error('Error creating zoom meeting:', error);
            const errorMessage = error.response?.data?.error || error.message || 'Failed to create zoom meeting';
            toast.error(errorMessage);
        } finally {
            setIsLoading(false);
        }
    };

    const handleDeleteMeeting = async (meetingId: string) => {
        if (!window.confirm('Are you sure you want to delete this meeting?')) return;

        try {
            setIsLoading(true);
            
            // Delete meeting using our backend API
            const response = await axios.post(`${API_ENDPOINT}/api/zoom/delete-meeting`, {
                userId: serviceProviderId,
                subId: subscriptionId,
                meetingId: meetingId
            });

            if (response.data.success) {
                toast.success('Meeting deleted successfully');
            } else {
                throw new Error(response.data.message || 'Failed to delete meeting');
            }
        } catch (error) {
            console.error('Error deleting meeting:', error);
            toast.error('Failed to delete meeting');
        } finally {
            setIsLoading(false);
        }
    };

    const handleUpdateMeeting = async (meetingId: string, updateData: {
        topic?: string;
        startTime?: string;
        duration?: number;
        description?: string;
        status?: string;
        type?: 'one-time' | 'recurring';
        weekDays?: number[];
        recurring?: boolean;
        recurringDays?: number[];
    }) => {
        try {
            setIsLoading(true);
            
            // Update meeting using our backend API
            const response = await axios.post(`${API_ENDPOINT}/api/zoom/update-meeting`, {
                userId: serviceProviderId,
                subId: subscriptionId,
                meetingId: meetingId,
                ...updateData
            });

            if (response.data.success) {
                toast.success('Meeting updated successfully');
                setShowCreateModal(false);
                setEditingMeetingId(null);
                setFormData({
                    topic: '',
                    startTime: new Date(),
                    duration: 60,
                    type: 'one-time',
                    weekDays: [],
                    description: '',
                    recurring: false,
                    recurringDays: []
                });
            } else {
                throw new Error(response.data.message || 'Failed to update meeting');
            }
        } catch (error) {
            console.error('Error updating meeting:', error);
            toast.error('Failed to update meeting');
        } finally {
            setIsLoading(false);
        }
    };

    const handleCopyMeetingInfo = (meeting: ZoomMeeting) => {
        const meetingInfo = `
Meeting Topic: ${meeting.topic}
Time: ${new Date(meeting.startTime).toLocaleString()}
Duration: ${meeting.duration} minutes
Meeting ID: ${meeting.meetingId}
Password: ${meeting.password}
Join URL: ${meeting.joinUrl}
        `.trim();

        navigator.clipboard.writeText(meetingInfo).then(() => {
            toast.success('Meeting info copied to clipboard');
        }).catch(() => {
            toast.error('Failed to copy meeting info');
        });
    };

    const formatDate = (timestamp: string) => {
        const date = new Date(timestamp);
        return date.toLocaleString('en-US', {
            weekday: 'short',
            month: 'short',
            day: 'numeric',
            year: 'numeric',
            hour: 'numeric',
            minute: '2-digit',
            hour12: true
        });
    };

    const generateCalendarEvent = (meeting: ZoomMeeting) => {
        const startTime = new Date(meeting.startTime);
        const endTime = new Date(startTime.getTime() + meeting.duration * 60 * 1000);
        
        const event = {
            title: meeting.topic,
            description: `${meeting.description || ''}\n\nJoin meeting: ${meeting.joinUrl}`,
            startTime: startTime.toISOString(),
            endTime: endTime.toISOString(),
            location: meeting.joinUrl
        };

        // Google Calendar link
        let googleCalendarUrl = `https://calendar.google.com/calendar/render?action=TEMPLATE&text=${encodeURIComponent(event.title)}&details=${encodeURIComponent(event.description)}&location=${encodeURIComponent(event.location)}&dates=${event.startTime.replace(/[-:]/g, '')}/${event.endTime.replace(/[-:]/g, '')}`;
        
        // Add recurrence rule for recurring meetings
        if (meeting.type === 'recurring' && meeting.weekDays && meeting.weekDays.length > 0) {
            const weekDays = meeting.weekDays.map(day => {
                const days = ['SU', 'MO', 'TU', 'WE', 'TH', 'FR', 'SA'];
                return days[day];
            }).join(',');
            googleCalendarUrl += `&recur=RRULE:FREQ=WEEKLY;BYDAY=${weekDays}`;
        }

        // iCal/Outlook format
        let icsContent = [
            'BEGIN:VCALENDAR',
            'VERSION:2.0',
            'PRODID:-//CoachPro//Zoom Integration//EN',
            'CALSCALE:GREGORIAN',
            'METHOD:PUBLISH',
            'BEGIN:VEVENT',
            `UID:${meeting.id}@coachpro.com`,
            `DTSTAMP:${new Date().toISOString().replace(/[-:]/g, '').split('.')[0]}Z`,
            `DTSTART:${event.startTime.replace(/[-:]/g, '').split('.')[0]}Z`,
            `DTEND:${event.endTime.replace(/[-:]/g, '').split('.')[0]}Z`,
            `SUMMARY:${event.title}`,
            `DESCRIPTION:${event.description.replace(/\n/g, '\\n')}`,
            `LOCATION:${event.location}`,
            `URL:${event.location}`
        ];

        if (meeting.type === 'recurring' && meeting.weekDays && meeting.weekDays.length > 0) {
            const weekDays = meeting.weekDays.map(day => {
                const days = ['SU', 'MO', 'TU', 'WE', 'TH', 'FR', 'SA'];
                return days[day];
            }).join(',');
            icsContent.push(`RRULE:FREQ=WEEKLY;BYDAY=${weekDays}`);
        }

        icsContent = icsContent.concat([
            'END:VEVENT',
            'END:VCALENDAR'
        ]);

        const icsString = icsContent.join('\r\n');
        const icsFile = new Blob([icsString], { type: 'text/calendar;charset=utf-8' });
        const icsUrl = URL.createObjectURL(icsFile);

        return {
            googleCalendarUrl,
            icsUrl
        };
    };

    const shareToWhatsApp = (meeting: ZoomMeeting) => {
        const text = `Join my Zoom Meeting: ${meeting.topic}\n\nTime: ${formatDate(meeting.startTime)}\nDuration: ${meeting.duration} minutes\n\nJoin using this link: ${meeting.joinUrl}`;
        const whatsappUrl = `https://wa.me/?text=${encodeURIComponent(text)}`;
        window.open(whatsappUrl, '_blank');
    };

    const handleEditMeeting = (meeting: ZoomMeeting) => {
        setFormData({
            topic: meeting.topic,
            startTime: new Date(meeting.startTime),
            duration: meeting.duration,
            type: meeting.type || 'one-time',
            weekDays: meeting.weekDays || [],
            description: meeting.description || '',
            recurring: meeting.type === 'recurring',
            recurringDays: meeting.weekDays || []
        });
        setEditingMeetingId(meeting.id);
        setShowCreateModal(true);
    };

    const handleSubmit = async () => {
        if (formData.type === 'recurring' && (!formData.weekDays || formData.weekDays.length === 0)) {
            toast.error('Please select at least one day for recurring meetings');
            return;
        }

        if (editingMeetingId) {
            // Update existing meeting
            await handleUpdateMeeting(editingMeetingId, {
                topic: formData.topic,
                startTime: formData.startTime.toISOString(),
                duration: formData.duration,
                description: formData.description,
                type: formData.type,
                weekDays: formData.weekDays,
                recurring: formData.type === 'recurring',
                recurringDays: formData.weekDays
            });
            setEditingMeetingId(null);
        } else {
            // Create new meeting
            await handleCreateMeeting();
        }
    };

    const handleJoinMeeting = async (meeting: ZoomMeeting) => {
        // Simply open the Zoom meeting URL in a new tab
        window.open(meeting.joinUrl, '_blank');
    };

    // Function to handle Zoom disconnect
    const handleZoomDisconnect = async () => {
        if (!serviceProviderId) return;

        // Check if user has permission to disconnect
        if (userRole && !['Administrator', 'Owner'].includes(userRole)) {
            toast.error('Only administrators and owners can disconnect Zoom');
            return;
        }

        setShowDisconnectModal(true);
    };

    const confirmZoomDisconnect = async () => {
        try {
            await dispatch(revokeZoomAccess(serviceProviderId));
            toast.success('Successfully disconnected from Zoom');
            setShowDisconnectModal(false);
            // Refresh zoom state
            dispatch(checkZoomAuth(serviceProviderId));
        } catch (error) {
            console.error('Error disconnecting Zoom:', error);
            toast.error('Failed to disconnect from Zoom');
        }
    };

    // Function to manually refresh Zoom status
    const handleRefreshZoomStatus = async () => {
        if (!serviceProviderId) return;
        
        setIsCheckingZoom(true);
        try {
            await dispatch(checkZoomAuth(serviceProviderId));
            toast.success('Zoom status refreshed');
        } catch (error) {
            console.error('Error refreshing Zoom status:', error);
            toast.error('Failed to refresh Zoom status');
        } finally {
            setIsCheckingZoom(false);
        }
    };

    return (
        <div className="bg-white rounded-lg shadow p-6 mb-6">
            {/* Improved Header Layout */}
            <div className="mb-6">
                {/* Title and Create Button Row */}
                <div className="flex flex-col sm:flex-row sm:justify-between sm:items-start gap-4 mb-4">
                    <h3 className="text-lg font-semibold">Zoom Meetings</h3>
                <Button
                    variant="contained"
                    color="primary"
                    startIcon={<VideocamIcon />}
                    onClick={() => setShowCreateModal(true)}
                        disabled={!zoomState.isAuthenticated}
                        size="small"
                        sx={{ 
                            minWidth: 'fit-content',
                            whiteSpace: 'nowrap'
                        }}
                >
                    Create Meeting
                </Button>
                </div>

                {/* Connection Status Section */}
                <div className="bg-gray-50 rounded-lg p-3 border">
                    {zoomState.isAuthenticated && zoomState.userEmail ? (
                        <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-3">
                            <div className="flex items-center space-x-2 text-green-600 text-sm">
                                <svg className="w-4 h-4 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                                </svg>
                                <span className="truncate">Connected as {zoomState.userEmail}</span>
                            </div>
                            <div className="flex items-center gap-3 flex-shrink-0">
                                <button
                                    onClick={handleRefreshZoomStatus}
                                    disabled={isCheckingZoom}
                                    className="text-xs text-blue-600 hover:text-blue-800 underline disabled:opacity-50 transition-colors"
                                    title="Refresh Zoom status"
                                >
                                    {isCheckingZoom ? 'Refreshing...' : 'Refresh'}
                                </button>
                                {(userRole === 'Administrator' || userRole === 'Owner' || !userRole) && (
                                    <button
                                        onClick={handleZoomDisconnect}
                                        className="text-xs text-red-600 hover:text-red-800 underline transition-colors"
                                    >
                                        Disconnect
                                    </button>
                                )}
                            </div>
                        </div>
                    ) : (
                        <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-3">
                            <div className="flex items-center space-x-2 text-orange-600 text-sm">
                                <svg className="w-4 h-4 animate-spin flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                                </svg>
                                <span>Checking Zoom connection...</span>
                            </div>
                            <button
                                onClick={handleRefreshZoomStatus}
                                disabled={isCheckingZoom}
                                className="text-xs text-blue-600 hover:text-blue-800 underline disabled:opacity-50 transition-colors flex-shrink-0"
                                title="Check Zoom status"
                            >
                                {isCheckingZoom ? 'Checking...' : 'Check Status'}
                            </button>
                        </div>
                    )}
                </div>
            </div>

            {isLoadingMeetings ? (
                <div className="flex justify-center items-center py-8">
                    <CircularProgress size={24} />
                </div>
            ) : meetings.length > 0 ? (
                <div className="space-y-3">
                    {meetings.map((meeting) => (
                        <div key={meeting.id} className="bg-gray-50 p-4 rounded-lg border border-gray-200">
                            <div className="flex justify-between items-start">
                                <div className="space-y-1">
                                    <div className="flex items-center gap-2">
                                        <h4 className="font-medium text-gray-900">{meeting.topic}</h4>
                                        {meeting.type === 'recurring' && (
                                            <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                                                <svg className="mr-1 h-3 w-3" viewBox="0 0 20 20" fill="currentColor">
                                                    <path fillRule="evenodd" d="M4 2a1 1 0 011 1v2.101a7.002 7.002 0 0111.601 2.566 1 1 0 11-1.885.666A5.002 5.002 0 005.999 7H9a1 1 0 010 2H4a1 1 0 01-1-1zm.008 9.057a1 1 0 011.276.61A5.002 5.002 0 0014.001 13H11a1 1 0 110-2h5a1 1 0 011 1v5a1 1 0 11-2 0v-2.101a7.002 7.002 0 01-11.601-2.566 1 1 0 01.61-1.276z" clipRule="evenodd" />
                                                </svg>
                                                Recurring
                                            </span>
                                        )}
                                    </div>
                                    <p className="text-sm text-gray-500">
                                        {formatDate(meeting.startTime)} • {meeting.duration} minutes
                                    </p>
                                    {meeting.description && (
                                        <p className="text-sm text-gray-700 mt-2">{meeting.description}</p>
                                    )}
                                </div>
                                <div className="flex items-center gap-2">
                                    <Tooltip title="Edit Meeting">
                                        <IconButton
                                            size="small"
                                            onClick={() => handleEditMeeting(meeting)}
                                            className="text-gray-600 hover:text-blue-600"
                                        >
                                            <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                                <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"></path>
                                                <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"></path>
                                            </svg>
                                        </IconButton>
                                    </Tooltip>
                                    <Tooltip title="Copy Meeting Info">
                                        <IconButton
                                            size="small"
                                            onClick={() => handleCopyMeetingInfo(meeting)}
                                            className="text-gray-600 hover:text-blue-600"
                                        >
                                            <ContentCopyIcon />
                                        </IconButton>
                                    </Tooltip>
                                    <Tooltip title="Delete Meeting">
                                        <IconButton
                                            size="small"
                                            onClick={() => handleDeleteMeeting(meeting.id)}
                                            className="text-gray-600 hover:text-red-600"
                                        >
                                            <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                                <path d="M3 6h18"></path>
                                                <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"></path>
                                            </svg>
                                        </IconButton>
                                    </Tooltip>
                                </div>
                            </div>
                            <div className="mt-3 flex items-center gap-4">
                                <button 
                                    onClick={() => handleJoinMeeting(meeting)}
                                    className="inline-flex items-center text-sm text-blue-600 hover:text-blue-800"
                                >
                                    <VideocamIcon className="mr-1" style={{ fontSize: 16 }} />
                                    Join Meeting
                                </button>
                                <a
                                    href={generateCalendarEvent(meeting).googleCalendarUrl}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="text-sm text-gray-600 hover:text-gray-800"
                                >
                                    Add to Google Calendar
                                </a>
                                <a
                                    href={generateCalendarEvent(meeting).icsUrl}
                                    download={`${meeting.topic}.ics`}
                                    className="text-sm text-gray-600 hover:text-gray-800"
                                >
                                    Download for Outlook/iCal
                                </a>
                                <button
                                    onClick={() => shareToWhatsApp(meeting)}
                                    className="text-sm text-gray-600 hover:text-gray-800"
                                >
                                    Share via WhatsApp
                                </button>
                            </div>
                        </div>
                    ))}
                </div>
            ) : (
                <div className="text-center py-8 text-gray-500">
                    No Zoom meetings scheduled yet
                </div>
            )}

            {!zoomState.isAuthenticated && (
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mt-4">
                    <div className="flex items-center space-x-2 text-blue-800">
                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                        </svg>
                        <span className="font-medium">Zoom Account Not Connected</span>
                    </div>
                    <p className="text-sm text-blue-700 mt-2">
                        To create and manage Zoom meetings, please connect your Zoom account in your Profile settings.
                    </p>
                    <button
                        onClick={() => window.location.href = '/profile'}
                        className="mt-3 inline-flex items-center px-3 py-2 border border-blue-300 shadow-sm text-sm leading-4 font-medium rounded-md text-blue-700 bg-white hover:bg-blue-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                    >
                        Go to Profile
                    </button>
                </div>
            )}

            {/* Zoom Disconnect Confirmation Modal */}
            <Dialog
                open={showDisconnectModal}
                onClose={() => setShowDisconnectModal(false)}
                maxWidth="sm"
                fullWidth
            >
                <DialogTitle sx={{ pb: 1 }}>
                    Disconnect Zoom Account
                </DialogTitle>
                <DialogContent>
                    <div className="py-2">
                        <div className="flex items-start space-x-3 mb-4">
                            <div className="flex-shrink-0">
                                <svg className="w-6 h-6 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z" />
                                </svg>
                            </div>
                            <div className="flex-1">
                                <p className="text-sm text-gray-700 mb-2">
                                    Are you sure you want to disconnect your Zoom account?
                                </p>
                                <p className="text-sm text-gray-600">
                                    This action will:
                                </p>
                                <ul className="list-disc list-inside text-sm text-gray-600 mt-2 space-y-1">
                                    <li>Remove access to create new Zoom meetings</li>
                                    <li>Disable Zoom integration for all subscriptions</li>
                                    <li>Existing meetings will remain but won't be manageable from this platform</li>
                                </ul>
                            </div>
                        </div>
                    </div>
                </DialogContent>
                <DialogActions sx={{ px: 3, pb: 3 }}>
                    <Button 
                        onClick={() => setShowDisconnectModal(false)}
                        variant="outlined"
                        color="inherit"
                    >
                        Cancel
                    </Button>
                    <Button
                        onClick={confirmZoomDisconnect}
                        variant="contained"
                        color="error"
                        sx={{
                            bgcolor: '#dc2626',
                            '&:hover': {
                                bgcolor: '#b91c1c',
                            },
                        }}
                    >
                        Disconnect
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Create/Edit Meeting Modal */}
            <Dialog
                open={showCreateModal}
                onClose={() => {
                    setShowCreateModal(false);
                    setEditingMeetingId(null);
                    setFormData({
                        topic: '',
                        startTime: new Date(),
                        duration: 60,
                        type: 'one-time',
                        weekDays: [],
                        description: '',
                        recurring: false,
                        recurringDays: []
                    });
                }}
                maxWidth="sm"
                fullWidth
            >
                <DialogTitle>
                    {editingMeetingId ? 'Edit Meeting' : 'Create New Meeting'}
                </DialogTitle>
                <DialogContent>
                    <div className="py-4 space-y-4">
                        <TextField
                            fullWidth
                            label="Meeting Topic"
                            value={formData.topic}
                            onChange={(e) => setFormData({ ...formData, topic: e.target.value })}
                            placeholder={`${subscriptionName} Meeting`}
                        />
                        
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">
                                Meeting Type
                            </label>
                            <div className="flex gap-3">
                                <button
                                    type="button"
                                    onClick={() => setFormData(prev => ({ 
                                        ...prev, 
                                        type: 'one-time', 
                                        weekDays: [],
                                        recurring: false,
                                        recurringDays: []
                                    }))}
                                    className={`flex-1 py-2 px-4 rounded-lg text-sm font-medium transition-colors
                                        ${formData.type === 'one-time'
                                            ? 'bg-blue-600 text-white'
                                            : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                                        }`}
                                >
                                    One-time Meeting
                                </button>
                                <button
                                    type="button"
                                    onClick={() => setFormData(prev => ({ 
                                        ...prev, 
                                        type: 'recurring',
                                        recurring: true
                                    }))}
                                    className={`flex-1 py-2 px-4 rounded-lg text-sm font-medium transition-colors
                                        ${formData.type === 'recurring'
                                            ? 'bg-blue-600 text-white'
                                            : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                                        }`}
                                >
                                    Recurring Meeting
                                </button>
                            </div>
                        </div>

                        <LocalizationProvider dateAdapter={AdapterDateFns}>
                            <DateTimePicker
                                label="Start Time"
                                value={formData.startTime}
                                onChange={(newValue) => {
                                    if (newValue) {
                                        setFormData({ ...formData, startTime: newValue });
                                    }
                                }}
                                slotProps={{
                                    textField: {
                                        fullWidth: true,
                                        variant: 'outlined'
                                    }
                                }}
                            />
                        </LocalizationProvider>

                        <TextField
                            fullWidth
                            type="number"
                            label="Duration (minutes)"
                            value={formData.duration}
                            onChange={(e) => setFormData({ ...formData, duration: parseInt(e.target.value) })}
                        />

                        {formData.type === 'recurring' && (
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">
                                    Recurring Days*
                                </label>
                                <div className="flex flex-wrap gap-2">
                                    {[
                                        { day: 'Sun', value: 0 },
                                        { day: 'Mon', value: 1 },
                                        { day: 'Tue', value: 2 },
                                        { day: 'Wed', value: 3 },
                                        { day: 'Thu', value: 4 },
                                        { day: 'Fri', value: 5 },
                                        { day: 'Sat', value: 6 }
                                    ].map(({ day, value }) => (
                                        <button
                                            key={value}
                                            type="button"
                                            onClick={() => {
                                                setFormData(prev => ({
                                                    ...prev,
                                                    weekDays: prev.weekDays?.includes(value)
                                                        ? prev.weekDays.filter(d => d !== value)
                                                        : [...(prev.weekDays || []), value],
                                                    recurringDays: prev.recurringDays?.includes(value)
                                                        ? prev.recurringDays.filter(d => d !== value)
                                                        : [...(prev.recurringDays || []), value]
                                                }));
                                            }}
                                            className={`px-3 py-2 rounded-lg text-sm font-medium transition-colors
                                                ${formData.weekDays?.includes(value)
                                                    ? 'bg-blue-600 text-white'
                                                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                                                }`}
                                        >
                                            {day}
                                        </button>
                                    ))}
                                </div>
                                {formData.weekDays?.length === 0 && (
                                    <p className="mt-1 text-sm text-gray-500">
                                        Select days when this meeting will occur
                                    </p>
                                )}
                            </div>
                        )}

                        <TextField
                            fullWidth
                            multiline
                            rows={3}
                            label="Description"
                            value={formData.description}
                            onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                        />
                    </div>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => {
                        setShowCreateModal(false);
                        setEditingMeetingId(null);
                        setFormData({
                            topic: '',
                            startTime: new Date(),
                            duration: 60,
                            type: 'one-time',
                            weekDays: [],
                            description: '',
                            recurring: false,
                            recurringDays: []
                        });
                    }}>
                        Cancel
                    </Button>
                    <Button
                        onClick={handleSubmit}
                        variant="contained"
                        disabled={isLoading || (formData.type === 'recurring' && formData.weekDays.length === 0)}
                        style={{ backgroundColor: '#2D8CFF' }}
                    >
                        {isLoading ? <CircularProgress size={24} /> : (editingMeetingId ? 'Update Meeting' : 'Create Meeting')}
                    </Button>
                </DialogActions>
            </Dialog>
        </div>
    );
};

export default ZoomMeetingSection; 